import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import LocationIcon from "../images/location-svgrepo-com.svg";
import { MapPin } from "lucide-react";
import GoogleMapReact from "google-map-react";

import { Splide, SplideSlide } from "@splidejs/react-splide";

import "@splidejs/react-splide/css";

import { SiteContext } from "../context/siteContext";

import StoreFilter from "./StoreFilter";

/* ============================================================================
   MATERIAL UI ICONS
   ============================================================================ */

import AtmIcon from "@mui/icons-material/Atm";
import BabyChangingStationIcon from "@mui/icons-material/BabyChangingStation";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalCarWashIcon from "@mui/icons-material/LocalCarWash";
import LocalLaundryServiceIcon from "@mui/icons-material/LocalLaundryService";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import WcIcon from "@mui/icons-material/Wc";

import Tooltip from "@mui/material/Tooltip";

/* ============================================================================
   SERVICE / BRAND IMAGES
   ============================================================================ */

import BP from "../images/bp-logo.webp";
import CHILLED_KEGS from "../images/CHILLED_KEGS.webp";
import Evri from "../images/Evri-logo.webp";
import Gulf from "../images/gulf-logo.webp";
import Hoagies from "../images/HoagiesLogo-small.webp";
import KEGS from "../images/KEGS.webp";
import Lottery from "../images/National-Lottery-Logo.webp";
import PayPoint from "../images/paypoint-logo.webp";
import SpeedQueen from "../images/sq.png";
import Subway from "../images/subway.webp";
import TubbeesLogo from "../images/Tubbees-logo.webp";
import V4u from "../images/v4uLogo.webp";
import Wish from "../images/wish-logo.webp";
import CoOpLogo from "../images/co-op-logo.png";
import PremierLogo from "../images/Premier-Logo.jpg";

function StoreMarker({ name, isActive, onClick }) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        position: "absolute",
        transform: "translate(-50%, -100%)",
        cursor: "pointer",
        zIndex: isActive ? 1000 : 10,
      }}
    >
      {/* Pin */}

      <div
        style={{
          width: isActive ? 34 : 30,
          height: isActive ? 34 : 30,
          borderRadius: "50% 50% 50% 0",
          transform: "rotate(-45deg)",
          background: isActive ? "#be1a1a" : "#ffffff",
          border: "3px solid #be1a1a",
          boxShadow: "0 3px 10px rgba(0,0,0,.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all .2s ease",
        }}
      >
        <div
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: isActive ? "#ffffff" : "#be1a1a",
            transform: "rotate(45deg)",
          }}
        />
      </div>

      {/* Store name */}

      <div
        style={{
          position: "absolute",
          left: "50%",
          bottom: 39,
          transform: "translateX(-50%)",
          background: "#ffffff",
          color: "#111827",
          padding: "5px 8px",
          borderRadius: 5,
          fontSize: 10,
          fontWeight: 700,
          whiteSpace: "nowrap",
          boxShadow: "0 2px 8px rgba(0,0,0,.18)",
          border: isActive ? "1px solid #be1a1a" : "1px solid #e5e7eb",
        }}
      >
        {name}
      </div>
    </div>
  );
}

/* ============================================================================
   SERVICE ICONS / LOGOS
   ============================================================================ */

function StoreServices({ marker }) {
  const services = marker?.services || marker?.utilities || {};

  const serviceItems = [
    {
      key: "atm",
      label: "ATM",
      type: "icon",
      icon: <AtmIcon fontSize="small" />,
    },

    {
      key: "babyChange",
      label: "Baby Change Station",
      type: "icon",
      icon: <BabyChangingStationIcon fontSize="small" />,
    },

    {
      key: "parking",
      label: "Parking on Site",
      type: "icon",
      icon: <LocalParkingIcon fontSize="small" />,
    },

    {
      key: "toilets",
      label: "Toilet Facilities",
      type: "icon",
      icon: <WcIcon fontSize="small" />,
    },

    {
      key: "jetWash",
      label: "Jet Washes & Air Towers",
      type: "icon",
      icon: <LocalCarWashIcon fontSize="small" />,
    },

    {
      key: "laundry",
      label: "Laundry Machines",
      type: "icon",
      icon: <LocalLaundryServiceIcon fontSize="small" />,
    },

    {
      key: "post",
      label: "Post Box",
      type: "icon",
      icon: <InventoryIcon fontSize="small" />,
    },

    {
      key: "PerfectDraftRewards",
      label: "PerfectDraft Rewards",
      type: "icon",
      icon: <EmojiEventsIcon fontSize="small" />,
    },

    {
      key: "speedQueen",
      label: "Speed Queen",
      type: "image",
      image: SpeedQueen,
    },

    {
      key: "bp",
      label: "BP Fuel",
      type: "image",
      image: BP,
    },

    {
      key: "tubbeesDessert",
      label: "Tubbees Dessert Lab",
      type: "image",
      image: TubbeesLogo,
    },

    {
      key: "tubbeesSlushee",
      label: "Tubbees Slushee",
      type: "image",
      image: TubbeesLogo,
    },

    {
      key: "hermes",
      label: "Evri Parcel Shop",
      type: "image",
      image: Evri,
    },

    {
      key: "wish",
      label: "Wish Parcel Shop",
      type: "image",
      image: Wish,
    },

    {
      key: "lottery",
      label: "National Lottery",
      type: "image",
      image: Lottery,
    },

    {
      key: "hoagies",
      label: "Hoagies Food To Go",
      type: "image",
      image: Hoagies,
    },

    {
      key: "v4u",
      label: "Vaped4U",
      type: "image",
      image: V4u,
    },

    {
      key: "gulf",
      label: "Gulf Fuel",
      type: "image",
      image: Gulf,
    },

    {
      key: "subway",
      label: "Subway",
      type: "image",
      image: Subway,
    },

    {
      key: "paypoint",
      label: "PayPoint",
      type: "image",
      image: PayPoint,
    },

    {
      key: "Kegs",
      label: "Kegs Available",
      type: "image",
      image: KEGS,
    },

    {
      key: "ChilledKegs",
      label: "Chilled Kegs Available",
      type: "image",
      image: CHILLED_KEGS,
    },

    {
      key: "premier",
      label: "Premier",
      type: "image",
      image: PremierLogo,
    },

    {
      key: "co_op",
      label: "Co-Op",
      type: "image",
      image: CoOpLogo,
    },
  ];

  const availableServices = serviceItems.filter(
    (service) => services?.[service.key] === true,
  );

  if (availableServices.length === 0) {
    return null;
  }

  return (
    <div className="store-card-services" onClick={(e) => e.stopPropagation()}>
      <div className="store-card-services-header">
        <span className="store-card-services-title">Available services</span>

        <span className="store-card-services-count">
          {availableServices.length}
        </span>
      </div>

      <div className="store-card-services-list">
        {availableServices.map((service) => (
          <Tooltip key={service.key} title={service.label} arrow>
            <div className="store-card-service" aria-label={service.label}>
              {service.type === "image" ? (
                <img src={service.image} alt={service.label} />
              ) : (
                service.icon
              )}
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

/* ============================================================================
   STORE CARD
   ============================================================================ */

function StoreCard({ _id, marker, isActive, onClick }) {
  const address = marker?.Address || {};

  const services = marker?.services || marker?.utilities || {};

  const phone =
    address?.phoneNumber || marker?.phoneNumber || marker?.phone || "";

  const email = address?.email || marker?.email || "";

  const postcode = address?.postcode || marker?.postcode || "";

  const storeUrl = marker?.url || marker?.website || marker?.storeUrl || "";

  const locationName = marker?.name || "Store";

  /* ==========================================================================
     ADDRESS
     ========================================================================== */

  const addressText = [
    address?.addressLine1,
    address?.addressLine2,
    address?.town,
    address?.city,
    address?.postcode,
  ]
    .filter(Boolean)
    .join(", ");

  /* ==========================================================================
     GOOGLE MAPS URL
     ========================================================================== */

  const googleMapsUrl = postcode
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${address?.addressLine1 || ""}, ${postcode}`,
      )}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        marker?.name || "",
      )}`;

  /* ==========================================================================
     SERVICE COUNT
     ========================================================================== */

  const serviceCount = Object.values(services).filter(Boolean).length;

  return (
    <article
      className={`store-card ${isActive ? "is-active" : ""}`}
      onClick={onClick}
    >
      {/* ================================================================
          CARD HEADER
      ================================================================ */}

      <div className="store-card-top">
        <div className="store-card-brand">
          <div className="store-card-brand-icon">
            <MapPin size={22} />
          </div>

          <div className="store-card-brand-text">
            <span>RaceTrack</span>

            <small>PITSTOP</small>
          </div>
        </div>

        {isActive && (
          <div className="store-card-selected">
            <span />
            Selected
          </div>
        )}
      </div>

      {/* ================================================================
          CARD CONTENT
      ================================================================ */}

      <div className="store-card-content">
        {/* ==============================================================
            STORE TITLE
        ============================================================== */}

        <div className="store-card-heading-row">
          <div>
            <div className="store-card-eyebrow">Store location</div>

            <h2 className="store-card-title">{locationName}</h2>
          </div>
        </div>

        {/* ==============================================================
            ADDRESS
        ============================================================== */}

        {addressText ? (
          <div className="store-card-detail">
            <div className="store-card-detail-icon">
              <img src={LocationIcon} alt="" aria-hidden="true" />
            </div>

            <div>
              <span className="store-card-detail-label">Address</span>

              <p>{addressText}</p>
            </div>
          </div>
        ) : (
          <div className="store-card-detail unavailable">
            <div className="store-card-detail-icon">
              <img src={LocationIcon} alt="" aria-hidden="true" />
            </div>

            <div>
              <span className="store-card-detail-label">Address</span>

              <p>Address information unavailable</p>
            </div>
          </div>
        )}

        {/* ==============================================================
            CONTACT INFORMATION
        ============================================================== */}

        {(phone || email) && (
          <div className="store-card-contact">
            {phone && (
              <div className="store-card-contact-item">
                <span className="store-card-contact-dot" />

                <span>{phone}</span>
              </div>
            )}

            {email && (
              <div className="store-card-contact-item">
                <span className="store-card-contact-dot" />

                <span className="store-card-email">{email}</span>
              </div>
            )}
          </div>
        )}

        {/* ==============================================================
            SERVICE ICONS
        ============================================================== */}

        <StoreServices marker={marker} />

        {/* ==============================================================
            EXISTING ACTION BUTTONS
        ============================================================== */}

        <div
          className="store-card-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Directions */}

          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="store-action primary"
          >
            Directions
          </a>

          {/* Call Store */}

          {phone ? (
            <a
              href={`tel:${phone.replace(/[^\d+]/g, "")}`}
              className="store-action"
            >
              Call Store
            </a>
          ) : (
            <span className="store-action disabled">Call Store</span>
          )}

          {/* View Store */}

          <button
            type="button"
            className="store-action"
            onClick={() => {
              window.location.href = `/${encodeURIComponent(marker.name)}`;
            }}
          >
            View Store
          </button>
        </div>
      </div>
    </article>
  );
}

/* ============================================================================
   DISTANCE CALCULATION
   ============================================================================ */

function calculateDistance(lat1, lng1, lat2, lng2) {
  const earthRadius = 6371;

  const dLat = ((lat2 - lat1) * Math.PI) / 180;

  const dLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;

  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/* ============================================================================
   MAIN DASHBOARD
   ============================================================================ */

export default function StoreDashboard() {
  const { markers } = useContext(SiteContext);

  const splideRef = useRef(null);

  const mapRef = useRef(null);

  const [selectedStore, setSelectedStore] = useState(null);

  const [postcode, setPostcode] = useState("");

  const [locationLoading, setLocationLoading] = useState(false);

  const [locationError, setLocationError] = useState("");

  const [serviceFilter, setServiceFilter] = useState("all");

  /* ==========================================================================
     MERGE MAP LOCATIONS WITH SITE DATA
     ========================================================================== */

  const stores = useMemo(() => {
    if (!Array.isArray(markers)) {
      return [];
    }

    return markers
      .map((marker) => {
        const latitude = Number(marker?.Address?.latitude);

        const longitude = Number(marker?.Address?.longitude);

        if (
          !marker?.name ||
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)
        ) {
          return null;
        }

        return {
          ...marker,
          name: marker.name,
          position: {
            lat: latitude,
            lng: longitude,
          },
        };
      })
      .filter(Boolean);
  }, [markers]);

  /* ==========================================================================
     FILTER STORES
     ========================================================================== */

  const filteredStores = useMemo(() => {
    if (serviceFilter === "all") {
      return stores;
    }

    return stores.filter((store) => {
      const utilities = store?.utilities || store?.services || {};

      return utilities[serviceFilter] === true;
    });
  }, [stores, serviceFilter]);

  /* ==========================================================================
     INITIAL STORE
     ========================================================================== */

  useEffect(() => {
    if (!stores.length) {
      return;
    }

    if (
      !selectedStore ||
      !stores.some((store) => store.name === selectedStore)
    ) {
      setSelectedStore(stores[0].name);
    }
  }, [stores, selectedStore]);

  /* ==========================================================================
     KEEP SELECTED STORE VALID AFTER FILTER
     ========================================================================== */

  useEffect(() => {
    if (!filteredStores.length) {
      setSelectedStore(null);
      return;
    }

    const selectedStillVisible = filteredStores.some(
      (store) => store.name === selectedStore,
    );

    if (!selectedStillVisible) {
      const first = filteredStores[0];

      setSelectedStore(first.name);

      if (mapRef.current && first.position) {
        mapRef.current.panTo(first.position);
      }
    }
  }, [filteredStores, selectedStore]);

  /* ==========================================================================
     SELECT STORE
     ========================================================================== */

  const selectStore = (storeName, moveMap = true, moveCarousel = true) => {
    setSelectedStore(storeName);

    const visibleIndex = filteredStores.findIndex(
      (store) => store.name === storeName,
    );

    const store = stores.find((item) => item.name === storeName);

    if (moveCarousel && splideRef.current?.splide && visibleIndex >= 0) {
      splideRef.current.splide.go(visibleIndex);
    }

    if (moveMap && mapRef.current && store?.position) {
      mapRef.current.panTo(store.position);

      mapRef.current.setZoom(13);
    }
  };

  /* ==========================================================================
     FIND NEAREST STORE
     ========================================================================== */

  const findNearestStore = () => {
    setLocationError("");

    if (!navigator.geolocation) {
      setLocationError("Location services are not supported by your browser.");

      return;
    }

    setLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        let nearestStore = null;

        let shortestDistance = Infinity;

        filteredStores.forEach((store) => {
          if (!store.position) {
            return;
          }

          const distance = calculateDistance(
            latitude,
            longitude,
            store.position.lat,
            store.position.lng,
          );

          if (distance < shortestDistance) {
            shortestDistance = distance;

            nearestStore = store;
          }
        });

        setLocationLoading(false);

        if (nearestStore) {
          selectStore(nearestStore.name);
        } else {
          setLocationError("No stores were found for the selected service.");
        }
      },

      () => {
        setLocationLoading(false);

        setLocationError(
          "We couldn't access your location. Please allow location access or search by postcode.",
        );
      },

      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      },
    );
  };

  /* ==========================================================================
     POSTCODE / TOWN SEARCH
     ========================================================================== */

  const searchByPostcode = async () => {
    const cleanPostcode = postcode.trim();

    if (!cleanPostcode) {
      setLocationError("Please enter a postcode or town.");

      return;
    }

    setLocationError("");

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&countrycodes=gb&limit=1&q=${encodeURIComponent(
          cleanPostcode,
        )}`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Location search failed");
      }

      const data = await response.json();

      if (!data.length) {
        setLocationError(
          "Location not found. Please check the postcode or town and try again.",
        );

        return;
      }

      const userLat = parseFloat(data[0].lat);

      const userLng = parseFloat(data[0].lon);

      let nearestStore = null;

      let shortestDistance = Infinity;

      filteredStores.forEach((store) => {
        if (!store.position) {
          return;
        }

        const distance = calculateDistance(
          userLat,
          userLng,
          store.position.lat,
          store.position.lng,
        );

        if (distance < shortestDistance) {
          shortestDistance = distance;

          nearestStore = store;
        }
      });

      if (nearestStore) {
        selectStore(nearestStore.name);
      } else {
        setLocationError("No stores were found for the selected service.");
      }
    } catch (error) {
      setLocationError("Unable to search this location right now.");
    }
  };

  /* ==========================================================================
     MAP CENTER
     ========================================================================== */

  const mapCenter = selectedStore
    ? stores.find((store) => store.name === selectedStore)?.position
    : {
        lat: 55.858,
        lng: -4.259,
      };

  /* ==========================================================================
     RENDER
     ========================================================================== */

  return (
    <section className="store-dashboard">
      {/* ================================================================
          PAGE HEADING
      ================================================================ */}

      <div className="store-page-heading">
        <h1>Find Your Nearest RaceTrack Pitstop</h1>
      </div>

      {/* ================================================================
          GOOGLE MAP
      ================================================================ */}

      <div className="store-map">
        <GoogleMapReact
          bootstrapURLKeys={{
            key: "AIzaSyCA17XufYl3oox1qRT6aXvvM8JqmyvGC1I",
          }}
          center={mapCenter}
          defaultCenter={{
            lat: 55.858,
            lng: -4.259,
          }}
          defaultZoom={11}
          options={{
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            zoomControl: true,
          }}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map }) => {
            mapRef.current = map;
          }}
        >
          {filteredStores.map((store) => (
            <StoreMarker
              key={store.name}
              lat={store.position.lat}
              lng={store.position.lng}
              name={store.name}
              isActive={selectedStore === store.name}
              onClick={() => selectStore(store.name)}
            />
          ))}
        </GoogleMapReact>
      </div>

      {/* ================================================================
          MAP GRADIENT
      ================================================================ */}

      <div className="store-map-gradient" />

      {/* ================================================================
          SEARCH / FILTER PANEL
      ================================================================ */}

      <div className="store-search-panel">
        <div className="store-search-title">Find a store</div>

        {/* Search */}

        <div className="store-search-row">
          <input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                searchByPostcode();
              }
            }}
            placeholder="Postcode or town"
            aria-label="Search by postcode or town"
          />

          <button
            type="button"
            onClick={searchByPostcode}
            className="store-search-button"
          >
            Search
          </button>
        </div>

        {/* ================================================================
            USE MY LOCATION
        ================================================================ */}

        <button
          type="button"
          onClick={findNearestStore}
          disabled={locationLoading}
          className="use-location-button"
        >
          <span>
            {locationLoading ? "Finding nearest store..." : "Use My Location"}
          </span>

          <img src={LocationIcon} alt="" aria-hidden="true" />
        </button>

        {/* ================================================================
            SERVICE FILTER
        ================================================================ */}

        <StoreFilter
          value={serviceFilter}
          onChange={setServiceFilter}
          filteredCount={filteredStores.length}
          totalCount={stores.length}
        />

        {/* Error */}

        {locationError && (
          <div className="store-search-error">{locationError}</div>
        )}
      </div>

      {/* ================================================================
          STORE CAROUSEL
      ================================================================ */}

      <div className="store-carousel">
        <div className="store-carousel-title">
          {serviceFilter === "all"
            ? "Our stores"
            : "Stores matching your filter"}
        </div>

        {filteredStores.length > 0 ? (
          <Splide
            ref={splideRef}
            options={{
              perPage: 4,
              perMove: 1,
              gap: "12px",
              pagination: false,
              arrows: true,
              drag: true,
              rewind: false,
              autoHeight: false,
              trimSpace: true,

              breakpoints: {
                1200: {
                  perPage: 3,
                },

                900: {
                  perPage: 2,
                },

                600: {
                  perPage: 1,
                  gap: "8px",
                },
              },
            }}
            onMoved={(splide) => {
              const store = filteredStores[splide.index];

              if (!store) {
                return;
              }

              setSelectedStore(store.name);

              if (mapRef.current && store.position) {
                mapRef.current.panTo(store.position);
              }
            }}
          >
            {filteredStores.map((marker) => (
              <SplideSlide key={marker.name}>
                <StoreCard
                  _id={marker._id}
                  marker={marker}
                  isActive={selectedStore === marker.name}
                  onClick={() => selectStore(marker.name)}
                />
              </SplideSlide>
            ))}
          </Splide>
        ) : (
          <div className="no-stores">No stores provide this service.</div>
        )}
      </div>

      {/* ================================================================
          STYLES
      ================================================================ */}

      <style>
        {`

          * {
            box-sizing: border-box;
          }


          /* ================================================================
             DASHBOARD
             ================================================================ */

          .store-dashboard {
            position: relative;
            width: 100%;
            height: 100vh;
            min-height: 600px;
            overflow: hidden;
            background: #e5e7eb;
          }


          /* ================================================================
             PAGE HEADING
             ================================================================ */

          .store-page-heading {
            position: absolute;
            top: 18px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 30;

            pointer-events: none;

            width: max-content;
            max-width:
              calc(100% - 30px);
          }

          .store-page-heading h1 {
            margin: 0;

            padding:
              8px 16px;

            border-radius: 8px;

            background:
              rgba(17,24,39,.88);

            color: #ffffff;

            font-size: 22px;
            line-height: 1.2;
            font-weight: 800;

            text-align: center;

            box-shadow:
              0 4px 14px
              rgba(0,0,0,.22);
          }


          /* ================================================================
             MAP
             ================================================================ */

          .store-map {
            position: absolute;
            inset: 0;

            width: 100%;
            height: 100%;
          }

          .store-map > div {
            width: 100% !important;
            height: 100% !important;
          }


          /* ================================================================
             MAP GRADIENT
             ================================================================ */

          .store-map-gradient {
            position: absolute;

            left: 0;
            right: 0;
            bottom: 0;

            height: 320px;

            z-index: 5;

            pointer-events: none;

            background:
              linear-gradient(
                to top,
                rgba(0,0,0,.62),
                rgba(0,0,0,.20),
                transparent
              );
          }


          /* ================================================================
             SEARCH PANEL
             ================================================================ */

          .store-search-panel {
            position: absolute;

            top: 85px;
            left: 20px;

            z-index: 30;

            width: 370px;
            max-width:
              calc(100% - 40px);

            padding: 15px;

            background:
              rgba(255,255,255,.98);

            border-radius: 12px;

            box-shadow:
              0 5px 22px
              rgba(0,0,0,.22);
          }

          .store-search-title {
            margin-bottom: 9px;

            color: #111827;

            font-size: 16px;
            line-height: 1.2;
            font-weight: 800;
          }

          .store-search-row {
            display: flex;

            gap: 7px;

            width: 100%;
          }

          .store-search-row input {
            flex: 1;

            min-width: 0;

            height: 40px;

            padding:
              0 11px;

            border:
              1px solid #cbd5e1;

            border-radius: 7px;

            outline: none;

            background: #ffffff;
            color: #111827;

            font-size: 13px;
          }

          .store-search-row input:focus {
            border-color: #be1a1a;

            box-shadow:
              0 0 0 2px
              rgba(190,26,26,.10);
          }

          .store-search-button {
            height: 40px;

            padding:
              0 15px;

            border: 0;

            border-radius: 7px;

            background: #be1a1a;
            color: #ffffff;

            font-size: 12px;
            font-weight: 800;

            cursor: pointer;
          }

          .store-search-button:hover {
            background: #991b1b;
          }


          /* ================================================================
             LOCATION BUTTON
             ================================================================ */

          .use-location-button {
            width: 100%;

            height: 38px;

            margin-top: 8px;

            display: flex;
            align-items: center;
            justify-content: center;

            gap: 8px;

            padding:
              0 12px;

            border:
              1px solid #be1a1a;

            border-radius: 7px;

            background: #ffffff;
            color: #be1a1a;

            font-size: 12px;
            font-weight: 800;

            cursor: pointer;
          }

          .use-location-button:hover {
            background: #fff5f5;
          }

          .use-location-button:disabled {
            opacity: .65;
            cursor: wait;
          }

          .use-location-button img {
            width: 20px;
            height: 20px;

            object-fit: contain;

            flex-shrink: 0;
          }


          /* ================================================================
             FILTER
             ================================================================ */

          .store-filter {
            margin-top: 11px;

            padding-top: 11px;

            border-top:
              1px solid #e5e7eb;
          }

          .store-filter-label {
            display: block;

            margin-bottom: 5px;

            color: #374151;

            font-size: 11px;
            line-height: 1.2;
            font-weight: 800;
          }

          .store-filter-select {
            width: 100%;

            height: 39px;

            padding:
              0 10px;

            border:
              1px solid #cbd5e1;

            border-radius: 7px;

            background: #ffffff;
            color: #111827;

            font-size: 13px;
            font-weight: 600;

            outline: none;

            cursor: pointer;

            box-shadow:
              none !important;
          }

          .store-filter-select:focus {
            border-color: #be1a1a;

            box-shadow:
              0 0 0 2px
              rgba(190,26,26,.10);
          }

          .store-filter-result {
            margin-top: 5px;

            color: #6b7280;

            font-size: 10px;
            line-height: 1.2;
          }

          .store-search-error {
            margin-top: 8px;

            padding:
              7px 8px;

            border-radius: 6px;

            background: #fef2f2;

            color: #b91c1c;

            font-size: 10px;
            line-height: 1.3;
          }


          /* ================================================================
             CAROUSEL
             ================================================================ */

          .store-carousel {
            position: absolute;

            left: 0;
            right: 0;
            bottom: 0;

            z-index: 20;

            padding:
              0 42px 18px;
          }

          .store-carousel-title {
            margin-bottom: 9px;

            color: #ffffff;

            font-size: 14px;
            line-height: 1.2;
            font-weight: 800;

            text-shadow:
              0 2px 6px
              rgba(0,0,0,.8);
          }

          .store-carousel .splide__track {
            overflow: visible;
          }

          .store-carousel .splide__list {
            align-items: stretch;
          }

          .store-carousel .splide__slide {
            height: auto;
          }


          /* ================================================================
             MODERN STORE CARD
             ================================================================ */

          .store-card {
            width: 100%;

            height: 315px;

            display: flex;
            flex-direction: column;

            box-sizing: border-box;

            border:
              1px solid
              rgba(255,255,255,.65);

            border-radius: 14px;

            background:
              linear-gradient(
                145deg,
                #ffffff 0%,
                #f8fafc 100%
              );

            color: #111827;

            box-shadow:
              0 8px 25px
              rgba(0,0,0,.20);

            overflow: hidden;

            cursor: pointer;

            transition:
              border-color .2s ease,
              box-shadow .2s ease,
              transform .2s ease;
          }

          .store-card:hover {
            transform:
              translateY(-3px);

            box-shadow:
              0 12px 30px
              rgba(0,0,0,.25);
          }

          .store-card.is-active {
            border:
              2px solid #be1a1a;

            box-shadow:
              0 12px 32px
              rgba(190,26,26,.30);
          }


          /* ================================================================
             CARD HEADER
             ================================================================ */

          .store-card-top {
            height: 62px;

            flex-shrink: 0;

            display: flex;

            align-items: center;
            justify-content: space-between;

            padding:
              0 14px;

            background:
              linear-gradient(
                135deg,
                #991b1b,
                #be1a1a 55%,
                #7f1d1d
              );

            color: #ffffff;
          }


          /* ================================================================
             BRAND
             ================================================================ */

          .store-card-brand {
            display: flex;

            align-items: center;

            gap: 9px;
          }

          .store-card-brand-icon {
            width: 34px;
            height: 34px;

            display: flex;

            align-items: center;
            justify-content: center;

            border-radius: 9px;

            background:
              rgba(255,255,255,.15);

            border:
              1px solid
              rgba(255,255,255,.25);

            box-shadow:
              inset 0 1px 1px
              rgba(255,255,255,.2);
          }

          .store-card-brand-icon span {
            width: 14px;
            height: 14px;

            display: block;

            border-radius:
              50% 50% 50% 0;

            background: #ffffff;

            transform:
              rotate(-45deg);
          }

          .store-card-brand-text {
            display: flex;

            flex-direction: column;

            line-height: 1;
          }

          .store-card-brand-text span {
            font-size: 14px;
            font-weight: 900;

            letter-spacing: .2px;
          }

          .store-card-brand-text small {
            margin-top: 3px;

            font-size: 8px;
            font-weight: 800;

            letter-spacing: 1.5px;

            opacity: .8;
          }


          /* ================================================================
             SELECTED BADGE
             ================================================================ */

          .store-card-selected {
            display: flex;

            align-items: center;

            gap: 5px;

            padding:
              5px 8px;

            border-radius: 20px;

            background:
              rgba(255,255,255,.15);

            border:
              1px solid
              rgba(255,255,255,.25);

            font-size: 9px;

            font-weight: 800;
          }

          .store-card-selected span {
            width: 6px;
            height: 6px;

            border-radius: 50%;

            background: #86efac;

            box-shadow:
              0 0 0 3px
              rgba(134,239,172,.18);
          }


          /* ================================================================
             CARD CONTENT
             ================================================================ */

          .store-card-content {
            display: flex;

            flex-direction: column;

            flex: 1;

            min-height: 0;

            padding:
              11px 13px 12px;
          }


          /* ================================================================
             HEADING
             ================================================================ */

          .store-card-heading-row {
            display: flex;

            align-items: flex-start;

            justify-content:
              space-between;

            gap: 10px;

            margin-bottom: 8px;
          }

          .store-card-eyebrow {
            margin-bottom: 2px;

            color: #9ca3af;

            font-size: 8px;

            line-height: 1.2;

            font-weight: 800;

            text-transform:
              uppercase;

            letter-spacing: .8px;
          }

          .store-card-title {
            margin: 0;

            color: #111827;

            font-size: 17px;

            line-height: 1.15;

            font-weight: 900;

            letter-spacing: -.2px;

            white-space: nowrap;

            overflow: hidden;

            text-overflow: ellipsis;
          }


          /* ================================================================
             SERVICE COUNT
             ================================================================ */


          .store-card-service-total span {
            margin-top: 3px;

            color:
              #991b1b;

            font-size: 7px;

            font-weight: 800;

            text-transform:
              uppercase;

            letter-spacing: .4px;
          }


          /* ================================================================
             ADDRESS
             ================================================================ */

          .store-card-detail {
            display: flex;

            align-items:
              flex-start;

            gap: 8px;

            padding:
              7px 8px;

            border-radius: 8px;

            background:
              #f8fafc;

            border:
              1px solid #eef2f7;
          }

          .store-card-detail-icon {
            width: 27px;
            height: 27px;

            flex-shrink: 0;

            display: flex;

            align-items: center;
            justify-content: center;

            border-radius: 7px;

            background:
              #ffffff;

            box-shadow:
              0 1px 4px
              rgba(0,0,0,.08);
          }

          .store-card-detail-icon img {
            width: 16px;
            height: 16px;
          }

          .store-card-detail-label {
            display: block;

            margin-bottom: 2px;

            color: #9ca3af;

            font-size: 7px;

            line-height: 1;

            font-weight: 800;

            text-transform:
              uppercase;

            letter-spacing: .7px;
          }

          .store-card-detail p {
            margin: 0;

            color: #374151;

            font-size: 9px;

            line-height: 1.35;

            font-weight: 600;

            display:
              -webkit-box;

            -webkit-line-clamp: 2;

            -webkit-box-orient:
              vertical;

            overflow: hidden;
          }

          .store-card-detail.unavailable p {
            color: #9ca3af;

            font-weight: 500;
          }


          /* ================================================================
             CONTACT DETAILS
             ================================================================ */

          .store-card-contact {
            display: flex;

            align-items: center;

            gap: 10px;

            margin-top: 6px;

            color: #4b5563;

            font-size: 8px;

            font-weight: 700;
          }

          .store-card-contact-item {
            display: flex;

            align-items: center;

            gap: 4px;

            min-width: 0;
          }

          .store-card-contact-dot {
            width: 5px;
            height: 5px;

            flex-shrink: 0;

            border-radius: 50%;

            background:
              #be1a1a;
          }

          .store-card-email {
            max-width: 140px;

            overflow: hidden;

            text-overflow: ellipsis;

            white-space: nowrap;
          }


          /* ================================================================
             SERVICES
             ================================================================ */

          .store-card-services {
            margin-top: 7px;

            padding-top: 7px;

            border-top:
              1px solid #e5e7eb;
          }

          .store-card-services-header {
            display: flex;

            align-items: center;

            justify-content:
              space-between;

            margin-bottom: 5px;
          }

          .store-card-services-title {
            color: #374151;

            font-size: 8px;

            font-weight: 900;

            text-transform:
              uppercase;

            letter-spacing: .65px;
          }

          .store-card-services-count {
            min-width: 17px;

            color:
              #be1a1a;

            font-size: 14px;
            height: 17px;

            display: flex;

            align-items: center;

            justify-content: center;

            padding:
              0 4px;

            border-radius: 5px;

            background:
              #f3f4f6;
            font-weight: 800;
          }

          .store-card-services-list {
            display: flex;

            align-items: center;

            gap: 5px;

            width: 100%;

            overflow-x: auto;

            overflow-y: hidden;

            padding-bottom: 2px;

            scrollbar-width:
              thin;

            scrollbar-color:
              #d1d5db transparent;
          }

          .store-card-services-list::-webkit-scrollbar {
            height: 3px;
          }

          .store-card-services-list::-webkit-scrollbar-track {
            background:
              transparent;
          }

          .store-card-services-list::-webkit-scrollbar-thumb {
            background:
              #d1d5db;

            border-radius:
              10px;
          }

          .store-card-service {
            width: 31px;
            height: 31px;

            flex:
              0 0 31px;

            display: flex;

            align-items: center;
            justify-content: center;

            border-radius: 7px;

            background:
              #ffffff;

            border:
              1px solid #e5e7eb;

            color:
              #be1a1a;

            box-shadow:
              0 1px 3px
              rgba(0,0,0,.06);

            transition:
              transform .15s ease,
              border-color .15s ease,
              box-shadow .15s ease;
          }

          .store-card-service {
          background: rgba(140, 28, 33, 0.45);
            border-color:
              #be1a1a;

            box-shadow:
              0 3px 8px
              rgba(190,26,26,.15);
          }

          .store-card-service img {
            max-width: 24px;
            max-height: 22px;

            width: auto;
            height: auto;

            object-fit:
              contain;
          }

          .store-card-service svg {
            width: 19px;
            height: 19px;
          }


          /* ================================================================
             ACTION BUTTONS
             ================================================================ */

          .store-card-actions {
            display: grid;

            grid-template-columns:
              repeat(3, 1fr);

            gap: 5px;

            margin-top: 8px;
          }

          .store-action {
            display: flex;

            align-items: center;
            justify-content: center;

            min-width: 0;

            height: 31px;

            padding:
              0 5px;

            border:
              1px solid #be1a1a;

            border-radius: 6px;

            background:
              #ffffff;

            color:
              #be1a1a;

            font-family:
              inherit;

            font-size: 9px;

            line-height: 1;

            font-weight: 900;

            text-decoration:
              none;

            text-align:
              center;

            cursor:
              pointer;

            transition:
              background .15s ease,
              color .15s ease,
              transform .15s ease;
          }

          .store-action:hover {
            background:
              #be1a1a;

            color:
              #ffffff;

            transform:
              translateY(-1px);
          }

          .store-action.primary {
            background:
              #be1a1a;

            color:
              #ffffff;
          }

          .store-action.primary:hover {
            background:
              #991b1b;
          }

          .store-action.disabled {
            border-color:
              #d1d5db;

            background:
              #f9fafb;

            color:
              #9ca3af;

            cursor:
              default;
          }

          .store-action.disabled:hover {
            transform:
              none;
          }


          /* ================================================================
             SPLIDE ARROWS
             ================================================================ */

          .store-carousel .splide__arrow {
            width: 31px;
            height: 31px;

            background:
              #ffffff;

            opacity: 1;

            box-shadow:
              0 3px 10px
              rgba(0,0,0,.3);
          }

          .store-carousel .splide__arrow svg {
            width: 11px;
            height: 11px;

            fill:
              #111827;
          }

          .store-carousel .splide__arrow--prev {
            left: -28px;
          }

          .store-carousel .splide__arrow--next {
            right: -28px;
          }


          /* ================================================================
             EMPTY RESULTS
             ================================================================ */

          .no-stores {
            padding:
              18px;

            border-radius:
              9px;

            background:
              #ffffff;

            color:
              #374151;

            font-size:
              13px;

            text-align:
              center;

            box-shadow:
              0 4px 15px
              rgba(0,0,0,.2);
          }


          /* ================================================================
             MOBILE
             ================================================================ */

          @media (max-width: 600px) {

            .store-dashboard {
              min-height:
                650px;
            }

            .store-page-heading {
              top: 10px;
              max-width:calc(100% - 20px);
            }

            .store-page-heading h1 {
              padding:7px 10px;
              font-size:16px;
            }

            .store-search-panel {
              top: 57px;
              margin-top: 30px;
              left: 10px;
              width:calc(100% - 20px);
              max-width:none;
              padding:11px;
              border-radius:9px;
            }

            .store-search-title {
              margin-bottom:
                7px;

              font-size:
                14px;
            }

            .store-search-row {
              gap:
                5px;
            }

            .store-search-row input {
              height:
                37px;

              font-size:
                12px;
            }

            .store-search-button {
              height:
                37px;

              padding:
                0 11px;

              font-size:
                11px;
            }

            .use-location-button {
              height:
                35px;

              font-size:
                11px;
            }

            .use-location-button img {
              width:
                18px;

              height:
                18px;
            }

            .store-filter {
              margin-top:
                8px;

              padding-top:
                8px;
            }

            .store-filter-select {
              height:
                36px;

              font-size:
                12px;
            }

            .store-map-gradient {
              height:
                270px;
            }

            .store-carousel {
              padding:
                0 12px 10px;
            }

            .store-carousel-title {
              margin-bottom:
                6px;

              font-size:
                12px;
            }

            .store-carousel .splide__arrow {
              display:
                none;
            }

            .store-carousel .splide__pagination {
              display:
                none;
            }

            .store-card {
              height:
                305px;

              border-radius:
                12px;
            }

            .store-card-top {
              height:
                56px;

              padding:
                0 11px;
            }

            .store-card-brand-icon {
              width:
                30px;

              height:
                30px;
            }

            .store-card-brand-text span {
              font-size:
                12px;
            }

            .store-card-brand-text small {
              font-size:
                7px;
            }

            .store-card-content {
              padding:
                9px 10px 10px;
            }

            .store-card-title {
              font-size:
                15px;
            }

            .store-card-service-total {
              min-width:
                40px;

              font-size:
                12px;
            }

            .store-card-detail {
              padding:
                6px;
            }

            .store-card-detail p {
              font-size:
                8px;
            }

            .store-card-service {
              width:
                29px;

              height:
                29px;

              flex-basis:
                29px;
            }

            .store-card-service img {
              max-width:
                22px;

              max-height:
                20px;
            }

            .store-card-actions {
              gap:
                4px;
            }

            .store-action {
              height:
                30px;

              font-size:
                8px;
            }

          }


          /* ================================================================
             SHORT DESKTOP SCREENS
             ================================================================ */

          @media
          (min-width: 601px)
          and (max-height: 700px) {

            .store-card {
              height:
                285px;
            }

            .store-card-top {
              height:
                52px;
            }

            .store-card-content {
              padding:
                8px 11px 9px;
            }

            .store-card-services {
              margin-top:
                5px;

              padding-top:
                5px;
            }

            .store-card-service {
              width:
                27px;

              height:
                27px;

              flex-basis:
                27px;
            }

            .store-card-actions {
              margin-top:
                6px;
            }

            .store-action {
              height:
                29px;
            }

            .store-search-panel {
              top:
                70px;
            }

            .store-page-heading {
              top:
                10px;
            }

            .store-page-heading h1 {
              font-size:
                18px;
            }

          }

        `}
      </style>
    </section>
  );
}
